import type {Thread, Comment} from '@github-ui/conversations'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {pullRequestMarkersKey, type Markers} from '../page-data/loaders/use-markers-data'
import {threadPreviewsQueryKey} from '../page-data/payloads/thread-previews'
import {diffSummariesKey} from '../page-data/payloads/diff-summaries'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'

type MutationData = {
  text: string
  inReplyTo: number | null | undefined
  submitBatch?: boolean
  path: string
  comparisonStartOid?: string | null | undefined
  comparisonEndOid?: string | null | undefined
}

export type MutationResponse = {
  thread: Thread
  comment: Comment
  totalCommentsCount: number
}

export function useCreateReplyCommentMutation(pullRequestPathName: string) {
  const queryClient = useQueryClient()
  const apiUrl = `${pullRequestPathName}/page_data/${PageData.createReviewComment}`

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })

      const json = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, json)
      return json
    },
    onSuccess: (data: MutationResponse, inputs: MutationData) => {
      // update the totalCommentCount for the TOC store
      queryClient.setQueryData(
        diffSummariesKey(pullRequestPathName),
        (oldTableofContentsData: PullRequestFileTreeDiff[]) => {
          return oldTableofContentsData?.map(diff => {
            if (diff.path !== inputs.path) {
              return diff
            }
            return {
              ...diff,
              totalCommentsCount: data.totalCommentsCount,
            }
          })
        },
      )

      // update the Markers Store
      queryClient.setQueryData(pullRequestMarkersKey(pullRequestPathName), (oldMarkersData: Markers) => {
        return {
          ...oldMarkersData,
          threads: {
            ...oldMarkersData?.threads,
            [data.thread.id]: {
              ...data.thread,
            },
          },
        }
      })

      // invalidate pullRequestFilesToolbar queries
      queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(pullRequestPathName),
      })
    },
  })
}
